import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { MemoryStorageService } from './memory-storage.service';

import { BehaviorSubject, Observable } from 'rxjs';
import { LocationScene, DESTINATION_VIDEO_SCENES, getDestinationScene } from '../models/destination-video-scenes';

export interface DestinationConfig {
  id: string;
  name: string;
  code: string;
  lat: number;
  lng: number;
  countries: string[];
}

export const DESTINATIONS: DestinationConfig[] = [
  {
    id: 'japan',
    name: 'Japan',
    code: 'jp',
    lat: 36.2048,
    lng: 138.2529,
    countries: ['Japan']
  },
  {
    id: 'malaysia',
    name: 'Malaysia',
    code: 'my',
    lat: 4.2105,
    lng: 101.9758,
    countries: ['Malaysia']
  },
  {
    id: 'vietnam',
    name: 'Vietnam',
    code: 'vn',
    lat: 14.0583,
    lng: 108.2772,
    countries: ['Vietnam']
  },
  {
    id: 'singapore',
    name: 'Singapore',
    code: 'sg',
    lat: 1.3521,
    lng: 103.8198,
    countries: ['Singapore']
  },
  {
    id: 'indonesia',
    name: 'Indonesia',
    code: 'id',
    lat: -8.3405,
    lng: 115.0920,
    countries: ['Indonesia']
  },
  {
    id: 'europe-1',
    name: 'Western Europe',
    code: 'eu1',
    lat: 49.8175,
    lng: 6.5,
    countries: ['France', 'Czechia', 'Netherlands']
  },
  {
    id: 'thailand',
    name: 'Thailand',
    code: 'th',
    lat: 15.8700,
    lng: 100.9925,
    countries: ['Thailand']
  },
  {
    id: 'europe-2',
    name: 'Southern Europe',
    code: 'eu2',
    lat: 45.4408,
    lng: 9.8,
    countries: ['Switzerland', 'Italy']
  },
  {
    id: 'dubai',
    name: 'Dubai',
    code: 'dxb',
    lat: 25.2048,
    lng: 55.2708,
    countries: ['United Arab Emirates']
  },
  {
    id: 'sri-lanka',
    name: 'Sri Lanka',
    code: 'lk',
    lat: 7.8731,
    lng: 80.7718,
    countries: ['Sri Lanka']
  },
  {
    id: 'andaman',
    name: 'Andaman',
    code: 'an',
    lat: 11.7401,
    lng: 92.6586,
    countries: ['India']
  },
  {
    id: 'mauritius',
    name: 'Mauritius',
    code: 'mu',
    lat: -20.3484,
    lng: 57.5522,
    countries: ['Mauritius']
  },
  {
    id: 'hong-kong',
    name: 'Hong Kong',
    code: 'hk',
    lat: 22.3193,
    lng: 114.1694,
    countries: ['Hong Kong']
  },
  {
    id: 'india',
    name: 'India',
    code: 'in',
    lat: 20.5937,
    lng: 78.9629,
    countries: ['India']
  },
  {
    id: 'north-india',
    name: 'North India',
    code: 'nin',
    lat: 34.1526,
    lng: 77.5771,
    countries: ['India']
  },
  {
    id: 'chardham',
    name: 'CharDham',
    code: 'cdm',
    lat: 30.7346,
    lng: 79.0669,
    countries: ['India']
  },
  {
    id: 'goa',
    name: 'Goa',
    code: 'goa',
    lat: 15.2993,
    lng: 74.1240,
    countries: ['India']
  },
  {
    id: 'south-india',
    name: 'South India',
    code: 'sin',
    lat: 10.0889,
    lng: 77.0595,
    countries: ['India']
  },
  {
    id: 'maldives',
    name: 'Maldives',
    code: 'mld',
    lat: 3.2028,
    lng: 73.2207,
    countries: ['Maldives']
  },
  {
    id: 'mumbai',
    name: 'Cordelia Cruises',
    code: 'bom',
    lat: 19.0760,
    lng: 72.8777,
    countries: ['India']
  },
  {
    id: 'global',
    name: 'Global Journey',
    code: 'gl',
    lat: 20.0,
    lng: 0.0,
    countries: []
  }
];

@Injectable({
  providedIn: 'root'
})
export class DestinationVideoService {
  private cacheName = 'mihuru-destination-videos-v1';
  private memoryBlobCache = new Map<string, string>();
  private supportedCodec: 'av1' | 'hevc' | 'h264' | null = null;
  private destinationSubject = new BehaviorSubject<string>('global');
  public destination$: Observable<string> = this.destinationSubject.asObservable();

  constructor() { }

  /**
   * Sets the active destination globally and notifies all listeners.
   */
  public setDestination(destInput: string): void {
    const dest = this.resolveDestination(destInput);
    if (typeof window !== 'undefined') {
      if (window.sessionStorage) {
        sessionStorage.setItem('selected_destination', dest.id);
      }
      try {
        MemoryStorageService.setItem('destination', dest.id);
      } catch (e) { }
    }
    this.destinationSubject.next(dest.id);
  }

  /**
   * Detects the highest quality video codec supported natively by the browser.
   */
  public getBestSupportedCodec(): 'av1' | 'hevc' | 'h264' {
    if (this.supportedCodec) {
      return this.supportedCodec;
    }

    if (typeof document === 'undefined') {
      return 'h264';
    }

    const video = document.createElement('video');

    // Check AV1 support
    const canPlayAv1 = video.canPlayType('video/mp4; codecs="av01.0.08M.08"') ||
      video.canPlayType('video/webm; codecs="av01.0.08M.08"');
    if (canPlayAv1 === 'probably') {
      this.supportedCodec = 'av1';
      return 'av1';
    }

    // Check HEVC (H.265) support - popular on Apple Safari / iOS devices
    const canPlayHevc = video.canPlayType('video/mp4; codecs="hvc1"') ||
      video.canPlayType('video/mp4; codecs="hev1"');
    if (canPlayHevc === 'probably' || canPlayHevc === 'maybe') {
      this.supportedCodec = 'hevc';
      return 'hevc';
    }

    // Check AV1 'maybe' fallback
    if (canPlayAv1 === 'maybe') {
      this.supportedCodec = 'av1';
      return 'av1';
    }

    // Universal H.264
    this.supportedCodec = 'h264';
    return 'h264';
  }

  /**
   * Returns whether the client screen is mobile width.
   */
  public isMobileDevice(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth <= 768;
  }

  /**
   * Resolves a destination input (name, ID, or fragment) to a canonical DestinationConfig.
   */
  public resolveDestination(destinationInput?: string | null): DestinationConfig {
    if (!destinationInput) {
      return DESTINATIONS.find(d => d.id === 'global')!;
    }

    const input = destinationInput.toLowerCase().trim();

    // Short code match (e.g., 'eu1', 'jp', 'dxb', 'sg')
    const byCode = DESTINATIONS.find(d => d.code === input);
    if (byCode) return byCode;

    // Direct ID match
    const byId = DESTINATIONS.find(d => d.id === input);
    if (byId) return byId;

    // Exact or partial name match
    const byName = DESTINATIONS.find(d => d.name.toLowerCase() === input);
    if (byName) return byName;

    // Specific aliases
    if (input.includes('singapore') || input.includes('singapour') || input.includes('changi') || input.includes('sentosa') || input === 'sg' || input === 'sin') {
      return DESTINATIONS.find(d => d.id === 'singapore')!;
    }
    if (input.includes('paris') || input.includes('prague') || input.includes('amsterdam') || input.includes('europe-1') || input.includes('west') || input.includes('north') || input.includes('france') || input.includes('netherlands')) {
      return DESTINATIONS.find(d => d.id === 'europe-1')!;
    }
    if (input.includes('zurich') || input.includes('venice') || input.includes('florence') || input.includes('rome') || input.includes('switzerland') || input.includes('europe-2') || input.includes('south') || input.includes('italy')) {
      return DESTINATIONS.find(d => d.id === 'europe-2')!;
    }
    if (input.includes('bali') || input.includes('indonesia') || input.includes('jakarta') || input.includes('denpasar')) {
      return DESTINATIONS.find(d => d.id === 'indonesia')!;
    }
    if (input.includes('nicobar') || input.includes('andaman') || input.includes('port blair')) {
      return DESTINATIONS.find(d => d.id === 'andaman')!;
    }
    if (input.includes('dubai') || input.includes('uae') || input.includes('emirates') || input.includes('abu dhabi')) {
      return DESTINATIONS.find(d => d.id === 'dubai')!;
    }
    if (input.includes('maldives') || input.includes('male') || input === 'mld') {
      return DESTINATIONS.find(d => d.id === 'maldives')!;
    }
    if (input.includes('tokyo') || input.includes('kyoto') || input.includes('osaka') || input.includes('japan')) {
      return DESTINATIONS.find(d => d.id === 'japan')!;
    }
    if (input.includes('kuala lumpur') || input.includes('malaysia') || input.includes('penang') || input.includes('langkawi')) {
      return DESTINATIONS.find(d => d.id === 'malaysia')!;
    }
    if (input.includes('vietnam') || input.includes('hanoi') || input.includes('da nang') || input.includes('ho chi minh')) {
      return DESTINATIONS.find(d => d.id === 'vietnam')!;
    }
    if (input.includes('bangkok') || input.includes('phuket') || input.includes('pattaya') || input.includes('thailand') || input.includes('krabi')) {
      return DESTINATIONS.find(d => d.id === 'thailand')!;
    }
    if (input.includes('sri lanka') || input.includes('colombo') || input.includes('kandy')) {
      return DESTINATIONS.find(d => d.id === 'sri-lanka')!;
    }
    if (input.includes('mauritius') || input.includes('port louis')) {
      return DESTINATIONS.find(d => d.id === 'mauritius')!;
    }
    if (input.includes('hong kong') || input.includes('hongkong') || input.includes('kowloon')) {
      return DESTINATIONS.find(d => d.id === 'hong-kong')!;
    }
    if (input.includes('north india') || input.includes('leh') || input.includes('ladakh') || input.includes('kashmir') || input.includes('himachal') || input.includes('manali') || input.includes('shimla') || input === 'nin') {
      return DESTINATIONS.find(d => d.id === 'north-india')!;
    }
    if (input.includes('chardham') || input.includes('chardhaam') || input.includes('char dham') || input.includes('chota chardham') || input.includes('kedarnath') || input.includes('badrinath') || input.includes('gangotri') || input.includes('yamunotri') || input.includes('uttarakhand') || input === 'cdm') {
      return DESTINATIONS.find(d => d.id === 'chardham')!;
    }
    if (input.includes('goa') || input.includes('panaji') || input.includes('calangute') || input.includes('baga') || input.includes('anjuna')) {
      return DESTINATIONS.find(d => d.id === 'goa')!;
    }
    if (input.includes('south india') || input.includes('kerala') || input.includes('munnar') || input.includes('kochi') || input.includes('alleppey') || input.includes('tamil nadu') || input.includes('karnataka') || input.includes('bengaluru') || input.includes('bangalore') || input.includes('chennai') || input.includes('ooty') || input === 'sin') {
      return DESTINATIONS.find(d => d.id === 'south-india')!;
    }
    if (input.includes('mumbai') || input.includes('bombay') || input === 'bom' || input.includes('cordelia') || input.includes('cruise')) {
      return DESTINATIONS.find(d => d.id === 'mumbai')!;
    }
    if (input === 'india' || input === 'in') {
      return DESTINATIONS.find(d => d.id === 'north-india')!;
    }

    // Broad search
    const partial = DESTINATIONS.find(d => input.includes(d.id) || d.name.toLowerCase().includes(input));
    if (partial) return partial;

    return DESTINATIONS.find(d => d.id === 'global')!;
  }

  /**
   * Returns URLs for poster and candidate videos based on destination, screen size, and codec.
   */
  public getVideoSources(destinationInput?: string | null, forceVariant?: 'desktop' | 'mobile'): {
    destination: DestinationConfig;
    variant: 'desktop' | 'mobile';
    primaryUrl: string;
    posterUrl: string;
    backgroundImageUrl: string;
    sources: Array<{ url: string; type: string }>;
  } {
    const destination = this.resolveDestination(destinationInput);
    const variant = forceVariant || (this.isMobileDevice() ? 'mobile' : 'desktop');
    const bestCodec = this.getBestSupportedCodec();

    const rootUrl = (environment as any).videoBaseUrl || 'assets/videos';
    const baseUrl = `${rootUrl}/${destination.id}/${variant}`;
    const primaryUrl = `${baseUrl}.${bestCodec}.mp4`;
    const backgroundImageUrl = `${rootUrl}/${destination.id}/${variant}_bg.jpg`;
    const posterUrl = backgroundImageUrl;

    const sources = [
      { url: `${baseUrl}.av1.mp4`, type: 'video/mp4; codecs="av01.0.08M.08"' },
      { url: `${baseUrl}.hevc.mp4`, type: 'video/mp4; codecs="hvc1"' },
      { url: `${baseUrl}.h264.mp4`, type: 'video/mp4; codecs="avc1.42E01E"' }
    ];

    return {
      destination,
      variant,
      primaryUrl,
      posterUrl,
      backgroundImageUrl,
      sources
    };
  }

  /**
   * Returns high-resolution background image URL for desktop or mobile.
   */
  public getBackgroundImageUrl(destinationInput?: string | null, forceVariant?: 'desktop' | 'mobile'): string {
    const destination = this.resolveDestination(destinationInput);
    const variant = forceVariant || (this.isMobileDevice() ? 'mobile' : 'desktop');
    const rootUrl = (environment as any).videoBaseUrl || 'assets/videos';
    return `${rootUrl}/${destination.id}/${variant}_bg.jpg`;
  }

  private inFlightFetches = new Map<string, Promise<string>>();

  /**
   * Fetches the video, caches it in Cache Storage, and returns a local Blob Object URL.
   * Deduplicates concurrent fetch requests for the same URL.
   */
  public async getCachedVideoBlobUrl(videoUrl: string): Promise<string> {
    // 1. In-memory blob check
    if (this.memoryBlobCache.has(videoUrl)) {
      return this.memoryBlobCache.get(videoUrl)!;
    }

    // 2. In-flight request deduplication
    if (this.inFlightFetches.has(videoUrl)) {
      return this.inFlightFetches.get(videoUrl)!;
    }

    const fetchPromise = this.fetchAndCacheBlob(videoUrl);
    this.inFlightFetches.set(videoUrl, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      this.inFlightFetches.delete(videoUrl);
    }
  }

  private async fetchAndCacheBlob(videoUrl: string): Promise<string> {
    // Browser Cache API check
    if (typeof window !== 'undefined' && 'caches' in window) {
      try {
        const cache = await caches.open(this.cacheName);
        let response = await cache.match(videoUrl);

        if (!response) {
          // Fetch from network and save to Cache Storage
          response = await fetch(videoUrl);
          if (response && response.ok) {
            await cache.put(videoUrl, response.clone());
          }
        }

        if (response && response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          this.memoryBlobCache.set(videoUrl, blobUrl);
          return blobUrl;
        }
      } catch (e) {
        console.warn('Cache API lookup/fetch failed, falling back to direct URL:', e);
      }
    }

    // Direct network fetch fallback
    try {
      const resp = await fetch(videoUrl);
      if (resp.ok) {
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        this.memoryBlobCache.set(videoUrl, blobUrl);
        return blobUrl;
      }
    } catch (e) {
      console.warn('Direct blob fetch failed, falling back to original URL:', e);
    }

    return videoUrl;
  }

  /**
   * Returns short animated GIF URLs for desktop and mobile for a given destination.
   */
  public getDestinationShortGif(destinationInput?: string | null): { desktopGif: string; mobileGif: string } {
    const destination = this.resolveDestination(destinationInput);
    const rootUrl = (environment as any).videoBaseUrl || 'assets/videos';
    const baseUrl = `${rootUrl}/${destination.id}`;
    return {
      desktopGif: `${baseUrl}/desk_short.gif`,
      mobileGif: `${baseUrl}/mobile_short.gif`
    };
  }

  /**
   * Retrieves currently selected destination ID from URL params, sessionStorage, MemoryStorage, or defaults to 'japan'.
   */
  public getSelectedDestination(): string {
    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const fromUrl = urlParams.get('d') || urlParams.get('dest') || urlParams.get('destination');
        if (fromUrl) {
          return this.resolveDestination(fromUrl).id;
        }
      } catch (e) { }

      if (window.sessionStorage) {
        const stored = sessionStorage.getItem('selected_destination') || sessionStorage.getItem('destination');
        if (stored) return this.resolveDestination(stored).id;
      }

      try {
        const mem = MemoryStorageService.getItem('destination') || MemoryStorageService.getItem('selected_destination');
        if (mem) return this.resolveDestination(mem).id;
      } catch (e) { }

      try {
        const local = localStorage.getItem('selected_destination') || localStorage.getItem('destination');
        if (local) return this.resolveDestination(local).id;
      } catch (e) { }
    }
    return 'global';
  }

  /**
   * Retrieves the location scene corresponding to the current video playback time and screen variant.
   */
  public getLocationScene(
    destinationInput?: string | null,
    forceVariant?: 'desktop' | 'mobile',
    currentTimeSeconds: number = 0,
    totalDurationSeconds: number = 20
  ): { scene: LocationScene | null; sceneIndex: number; isVisible: boolean } {
    const dest = this.resolveDestination(destinationInput);
    const variant = forceVariant || (this.isMobileDevice() ? 'mobile' : 'desktop');
    return getDestinationScene(dest.id, variant, currentTimeSeconds, totalDurationSeconds);
  }

  /**
   * Retrieves all 4 location scenes for a destination.
   */
  public getLocationScenes(destinationInput?: string | null, forceVariant?: 'desktop' | 'mobile'): LocationScene[] {
    const dest = this.resolveDestination(destinationInput);
    const variant = forceVariant || (this.isMobileDevice() ? 'mobile' : 'desktop');
    const config = DESTINATION_VIDEO_SCENES[dest.id] || DESTINATION_VIDEO_SCENES['global'];
    return (variant === 'mobile' ? config.mobile : config.desktop) || config.desktop;
  }
}
